import { callObsidianAI } from '../utils/obsidian';
import React, { useState, useEffect } from 'react';
import { MdVideoLibrary, MdCloudUpload, MdBarChart, MdTrendingUp, MdCheckCircle, MdError, MdPending, MdAutoAwesome, MdLock, MdLiveTv } from 'react-icons/md';
import { FaYoutube, FaFacebook, FaTiktok, FaInstagram } from 'react-icons/fa';
import { getOverview, getPlatformAnalytics, getVideos, getChannels } from '../utils/api';

const platformConfig = {
  youtube: {
    icon: <FaYoutube style={{ color: '#ff0000', fontSize: '24px' }} />,
    label: 'YouTube', unitLabel: 'Channels',
    color: '#ff0000', bg: 'rgba(255,0,0,0.1)', border: 'rgba(255,0,0,0.2)'
  },
  facebook: {
    icon: <FaFacebook style={{ color: '#1877f2', fontSize: '24px' }} />,
    label: 'Facebook', unitLabel: 'Pages',
    color: '#1877f2', bg: 'rgba(24,119,242,0.1)', border: 'rgba(24,119,242,0.2)'
  },
  tiktok: {
    icon: <FaTiktok style={{ color: '#e0e0e0', fontSize: '24px' }} />,
    label: 'TikTok', unitLabel: 'IDs',
    color: '#e0e0e0', bg: 'rgba(255,255,255,0.05)', border: 'rgba(255,255,255,0.1)'
  },
  instagram: {
    icon: <FaInstagram style={{ color: '#e1306c', fontSize: '24px' }} />,
    label: 'Instagram', unitLabel: 'Profiles',
    color: '#e1306c', bg: 'rgba(225,48,108,0.1)', border: 'rgba(225,48,108,0.2)'
  }
};

const livePlatformConfig = {
  youtube: {
    icon: <FaYoutube style={{ color: '#ff0000', fontSize: '16px' }} />,
    color: '#ff0000', bg: 'rgba(255,0,0,0.08)', border: 'rgba(255,0,0,0.2)'
  },
  facebook: {
    icon: <FaFacebook style={{ color: '#1877f2', fontSize: '16px' }} />,
    color: '#1877f2', bg: 'rgba(24,119,242,0.08)', border: 'rgba(24,119,242,0.2)'
  },
  tiktok: {
    icon: <FaTiktok style={{ color: '#e0e0e0', fontSize: '16px' }} />,
    color: '#e0e0e0', bg: 'rgba(255,255,255,0.04)', border: 'rgba(255,255,255,0.1)'
  },
  instagram: {
    icon: <FaInstagram style={{ color: '#e1306c', fontSize: '16px' }} />,
    color: '#e1306c', bg: 'rgba(225,48,108,0.08)', border: 'rgba(225,48,108,0.2)'
  }
};

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return { text: 'Good Morning', emoji: '\uD83C\uDF05' };
  if (hour < 17) return { text: 'Good Afternoon', emoji: '\u2600\uFE0F' };
  return { text: 'Good Evening', emoji: '\uD83C\uDF19' };
};

const LiveStatsMini = ({ setActivePage }) => {
  const [selectedPlatform, setSelectedPlatform] = useState('youtube');
  const config = livePlatformConfig[selectedPlatform];

  const stats = [
    { label: 'Views Last Hour', icon: '\uD83D\uDC41' },
    { label: 'Watch Time', icon: '\u23F1' },
    { label: 'Last 48hrs', icon: '\uD83D\uDCC8' },
    { label: 'New Followers', icon: '\uD83D\uDC65' }
  ];

  return (
    <div className="card">
      <div className="card-header">
        <span className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <MdLiveTv style={{ color: 'var(--primary)' }} />
          Live Stats
        </span>
        <div style={{ display: 'flex', gap: '6px' }}>
          {Object.entries(livePlatformConfig).map(([platform, cfg]) => (
            <button
              key={platform}
              onClick={() => setSelectedPlatform(platform)}
              style={{
                background: selectedPlatform === platform ? cfg.bg : 'transparent',
                border: '1px solid ' + (selectedPlatform === platform ? cfg.border : 'var(--border)'),
                borderRadius: '8px', padding: '4px 8px',
                cursor: 'pointer', display: 'flex', alignItems: 'center',
                transition: 'all 0.2s ease'
              }}
            >
              {cfg.icon}
            </button>
          ))}
        </div>
      </div>

      <div style={{
        background: config.bg, border: '1px solid ' + config.border,
        borderRadius: '12px', padding: '12px', marginBottom: '12px',
        display: 'flex', alignItems: 'center', gap: '8px'
      }}>
        {config.icon}
        <span style={{ fontSize: '13px', fontWeight: '600', color: config.color, textTransform: 'capitalize' }}>
          {selectedPlatform}
        </span>
        <div style={{
          marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '4px',
          background: 'rgba(239,68,68,0.15)', padding: '3px 8px',
          borderRadius: '10px', border: '1px solid rgba(239,68,68,0.3)'
        }}>
          <div style={{
            width: '6px', height: '6px', borderRadius: '50%',
            background: '#ef4444', animation: 'pulse-red 1.5s infinite'
          }} />
          <span style={{ fontSize: '11px', color: '#ef4444', fontWeight: '700' }}>LIVE</span>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', marginBottom: '12px' }}>
        {stats.map((stat, i) => (
          <div key={i} style={{
            background: 'var(--bg-dark)', borderRadius: '10px',
            padding: '12px', border: '1px solid var(--border)',
            position: 'relative', overflow: 'hidden'
          }}>
            <div style={{
              position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(2px)',
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              zIndex: 2, borderRadius: '10px'
            }}>
              <MdLock style={{ fontSize: '14px', color: 'var(--primary)', marginBottom: '4px' }} />
              <span style={{ fontSize: '10px', color: 'var(--text-secondary)', textAlign: 'center' }}>
                After OAuth
              </span>
            </div>
            <div style={{ opacity: 0.2 }}>
              <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                {stat.icon} {stat.label}
              </div>
              <div style={{ fontSize: '20px', fontWeight: '800', color: config.color }}>-</div>
            </div>
          </div>
        ))}
      </div>

      <div onClick={() => setActivePage('analytics')} style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
        padding: '10px', background: config.bg, border: '1px solid ' + config.border,
        borderRadius: '10px', textDecoration: 'none', fontSize: '13px',
        fontWeight: '600', color: config.color, transition: 'all 0.2s ease', cursor: 'pointer'
      }}>
        View Full Analytics
      </div>

      <style>{`
        @keyframes pulse-red {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
      `}</style>
    </div>
  );
};

const NicheAdvisor = () => {
  const [query, setQuery] = useState('');
  const [response, setResponse] = useState('');
  const [loading, setLoading] = useState(false);
  const [platform, setPlatform] = useState('youtube');

  const askObsidian = async () => {
    if (!query.trim()) return;
    setLoading(true);
    setResponse('');
    try {
      const data = await callObsidianAI(
        [{ role: 'user', content: 'Platform focus: ' + platform + '. Question: ' + query + '. Give a concise actionable response focused on ' + platform + ' specifically.' }],
        'You are Obsidian AI on the Vantage platform. You are STRICTLY limited to answering questions about social media content strategy, niche research, SEO, keywords, growth tactics, monetisation and channel building for YouTube, Facebook, TikTok and Instagram ONLY.',
        800
      );
      setResponse(data.content[0].text);
    } catch (error) {
      setResponse('Failed to get response. Please check your API key.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card">
      <div className="card-header">
        <span className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <MdAutoAwesome style={{ color: 'var(--primary)' }} />
          Obsidian AI
        </span>
        <span className="ai-badge">AI</span>
      </div>
      <div style={{ marginBottom: '12px' }}>
        <select className="form-select" value={platform}
          onChange={(e) => setPlatform(e.target.value)} style={{ marginBottom: '10px' }}>
          <option value="youtube">YouTube</option>
          <option value="facebook">Facebook</option>
          <option value="tiktok">TikTok</option>
          <option value="instagram">Instagram</option>
        </select>
        <div style={{ display: 'flex', gap: '8px' }}>
          <input className="form-control"
            placeholder={'Ask about ' + platform + '...'}
            value={query} onChange={(e) => setQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && askObsidian()} />
          <button className="btn btn-primary" onClick={askObsidian}
            disabled={loading || !query.trim()} style={{ flexShrink: 0 }}>
            {loading
              ? <div className="spinner" style={{ width: '16px', height: '16px', borderWidth: '2px' }} />
              : <MdAutoAwesome />}
          </button>
        </div>
      </div>
      <div style={{
        minHeight: '180px', maxHeight: '220px', overflowY: 'auto',
        background: 'var(--bg-dark)', borderRadius: '10px',
        padding: '14px', border: '1px solid var(--border)',
        fontSize: '13px', lineHeight: '1.7', color: 'var(--text-secondary)', whiteSpace: 'pre-wrap'
      }}>
        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--primary)' }}>
            <div className="spinner" style={{ width: '16px', height: '16px', borderWidth: '2px' }} />
            Thinking...
          </div>
        ) : response ? (
          <div style={{ color: 'var(--text-primary)', whiteSpace: 'pre-wrap', lineHeight: '1.7' }} dangerouslySetInnerHTML={{ __html: response.replace(/#{1,6}\s/g, '').replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/\*(.*?)\*/g, '<em>$1</em>').replace(/^- /gm, '• ').replace(/^\d+\. /gm, '') }} />
        ) : (
          <div style={{ textAlign: 'center', paddingTop: '40px', color: 'var(--text-light)' }}>
            <MdAutoAwesome style={{ fontSize: '28px', marginBottom: '8px', color: 'var(--primary)', opacity: 0.4 }} />
            <p style={{ fontSize: '12px' }}>Ask about niches, strategy and growth</p>
          </div>
        )}
      </div>
    </div>
  );
};

const TrendingVideos = ({ setActivePage }) => {
  const [videos, setVideos] = useState([]);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);

  const platformIcons = {
    youtube: <FaYoutube style={{ color: '#ff0000', fontSize: '14px' }} />,
    facebook: <FaFacebook style={{ color: '#1877f2', fontSize: '14px' }} />,
    tiktok: <FaTiktok style={{ color: '#e0e0e0', fontSize: '14px' }} />,
    instagram: <FaInstagram style={{ color: '#e1306c', fontSize: '14px' }} />
  };

  useEffect(() => {
    const fetchTrending = async () => {
      try {
        const [videosRes, channelsRes] = await Promise.all([getVideos(), getChannels()]);
        setVideos((videosRes.data.data || []).slice(0, 6));
        setChannels(channelsRes.data.data || []);
      } catch (error) {
        console.error('Failed to load trending videos');
      } finally {
        setLoading(false);
      }
    };
    fetchTrending();
  }, []);

  const getChannelInfo = (channelId) => channels.find(c => c.id === channelId);

  if (loading) return (
    <div className="card" style={{ padding: '40px', textAlign: 'center' }}>
      <div className="spinner" style={{ margin: '0 auto' }}></div>
    </div>
  );

  if (videos.length === 0) return (
    <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
      <MdVideoLibrary style={{ fontSize: '48px', color: 'var(--primary)', opacity: 0.3, marginBottom: '12px' }} />
      <p style={{ color: 'var(--text-secondary)' }}>No videos yet. Upload your first video to see trending analytics.</p>
      <button onClick={() => setActivePage('upload')} className="btn btn-primary" style={{ marginTop: '16px', display: 'inline-flex' }}>
        <MdCloudUpload /> Upload Video
      </button>
    </div>
  );

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
      {videos.map((video, index) => {
        const channel = getChannelInfo(video.channel_id);
        return (
          <div key={video.id} style={{
            background: 'var(--bg-card)', borderRadius: '14px',
            border: '1px solid var(--border)', overflow: 'hidden',
            transition: 'all 0.3s ease'
          }}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor = 'var(--border-green)';
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = 'var(--shadow-green)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = 'var(--border)';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <div style={{
              height: '120px', background: 'var(--bg-dark)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              position: 'relative', overflow: 'hidden'
            }}>
              {video.thumbnail_path ? (
                <img src={video.thumbnail_path} alt="thumbnail"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  onError={e => { e.target.style.display = 'none'; }} />
              ) : (
                <MdVideoLibrary style={{ fontSize: '40px', color: 'var(--border)' }} />
              )}
              <div style={{
                position: 'absolute', top: '8px', left: '8px',
                background: 'rgba(0,0,0,0.7)', borderRadius: '6px',
                padding: '3px 8px', fontSize: '11px', fontWeight: '700', color: 'white'
              }}>
                #{index + 1}
              </div>
              <div style={{ position: 'absolute', top: '8px', right: '8px' }}>
                <span className={'badge badge-' + (video.status === 'published' ? 'success' : 'warning')}
                  style={{ fontSize: '10px', padding: '2px 6px' }}>
                  {video.status}
                </span>
              </div>
            </div>
            <div style={{ padding: '14px' }}>
              <div style={{
                fontWeight: '600', fontSize: '13px', color: 'var(--text-primary)',
                marginBottom: '6px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
              }}>
                {video.youtube_title || video.title || 'Untitled'}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
                {platformIcons[channel?.platform]}
                <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                  {channel?.name || 'Unknown Channel'}
                </span>
              </div>
              <div style={{
                background: 'rgba(0,0,0,0.3)', borderRadius: '8px', padding: '8px',
                display: 'flex', justifyContent: 'space-between',
                fontSize: '11px', color: 'var(--text-light)', border: '1px solid var(--border)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <MdLock style={{ fontSize: '12px' }} />
                  <span>Live after OAuth</span>
                </div>
                {video.claude_ai_used && (
                  <span className="ai-badge" style={{ fontSize: '10px', padding: '1px 6px' }}>AI</span>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const Dashboard = ({ setActivePage }) => {
  const [overview, setOverview] = useState(null);
  const [platforms, setPlatforms] = useState(null);
  const [loading, setLoading] = useState(true);
  const greeting = getGreeting();

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [overviewRes, platformRes] = await Promise.all([
        getOverview(),
        getPlatformAnalytics()
      ]);
      setOverview(overviewRes.data.data);
      setPlatforms(platformRes.data.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading">
          <div className="spinner"></div>
          Loading Dashboard...
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">

      <div style={{
        background: 'linear-gradient(135deg, rgba(0,200,83,0.1), rgba(29,233,182,0.05))',
        border: '1px solid rgba(0,200,83,0.2)', borderRadius: '20px',
        padding: '32px', marginBottom: '32px', position: 'relative', overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute', top: '-20px', right: '-20px',
          width: '200px', height: '200px',
          background: 'radial-gradient(circle, rgba(0,200,83,0.1), transparent)', borderRadius: '50%'
        }} />
        <p style={{
          fontSize: '16px', color: 'var(--text-secondary)',
          marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px'
        }}>
          {greeting.emoji} {greeting.text}
        </p>
        <h1 style={{ fontSize: '42px', fontWeight: '800', lineHeight: '1.1', marginBottom: '12px' }}>
          Welcome to <span className="gradient-text">Vantage</span>
        </h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: '16px', maxWidth: '600px' }}>
          Your edge over every platform. Upload once, reach everywhere, and dominate every algorithm simultaneously.
        </p>
        <div style={{ display: 'flex', gap: '12px', marginTop: '20px', flexWrap: 'wrap' }}>
          <button onClick={() => setActivePage('upload')} className="btn btn-primary">
            <MdCloudUpload /> Upload Video
          </button>
          <button onClick={() => setActivePage('obsidian')} className="btn btn-secondary">
            <MdAutoAwesome /> Obsidian AI
          </button>
        </div>
      </div>

      <div style={{ marginBottom: '32px' }}>
        <h2 style={{
          fontSize: '20px', fontWeight: '700', marginBottom: '16px',
          color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px'
        }}>
          <MdBarChart style={{ color: 'var(--primary)' }} /> Platform Overview
        </h2>
        <div className="grid-4">
          {platforms && Object.entries(platforms).map(([platform, data]) => {
            const config = platformConfig[platform];
            if (!config) return null;
            return (
              <div key={platform} onClick={() => setActivePage('channels')} style={{
                background: config.bg, border: '1px solid ' + config.border,
                borderRadius: '16px', padding: '20px', transition: 'all 0.3s ease', cursor: 'pointer'
              }}
                onMouseEnter={e => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 8px 24px ' + config.border;
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                  {config.icon}
                  <span style={{ fontWeight: '700', fontSize: '16px', color: 'var(--text-primary)' }}>
                    {config.label}
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                      Active {config.unitLabel}
                    </span>
                    <span style={{ fontWeight: '700', fontSize: '18px', color: config.color }}>
                      {data.active_channels}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Published</span>
                    <span style={{ fontWeight: '600', fontSize: '15px', color: 'var(--primary)' }}>
                      {data.published_videos}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Pending</span>
                    <span style={{ fontWeight: '600', fontSize: '15px', color: '#f59e0b' }}>
                      {data.pending_uploads}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid-2" style={{ marginBottom: '20px' }}>
        <div className="card">
          <div className="card-header">
            <span className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <MdBarChart style={{ color: 'var(--primary)' }} /> Overall Statistics
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {[
              { label: 'Total Videos', value: overview?.total_videos || 0, icon: <MdVideoLibrary />, color: '#00bcd4' },
              { label: 'Published', value: overview?.published_videos || 0, icon: <MdCheckCircle />, color: '#00c853' },
              { label: 'Scheduled', value: overview?.scheduled_videos || 0, icon: <MdPending />, color: '#f59e0b' },
              { label: 'Drafts', value: overview?.draft_videos || 0, icon: <MdPending />, color: '#64748b' },
              { label: 'Failed', value: overview?.failed_videos || 0, icon: <MdError />, color: '#ef4444' },
            ].map((stat, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '10px 14px', background: 'var(--bg-dark)',
                borderRadius: '10px', border: '1px solid var(--border)', transition: 'all 0.2s ease'
              }}
                onMouseEnter={e => {
                  e.currentTarget.style.borderColor = 'rgba(0,200,83,0.3)';
                  e.currentTarget.style.transform = 'translateX(4px)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.borderColor = 'var(--border)';
                  e.currentTarget.style.transform = 'translateX(0)';
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ color: stat.color, fontSize: '16px' }}>{stat.icon}</span>
                  <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{stat.label}</span>
                </div>
                <span style={{ fontWeight: '700', fontSize: '18px', color: stat.color }}>{stat.value}</span>
              </div>
            ))}
          </div>
        </div>
        <LiveStatsMini setActivePage={setActivePage} />
      </div>

      <div className="grid-2" style={{ marginBottom: '32px' }}>
        <NicheAdvisor />
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div onClick={() => setActivePage('scorer')} style={{ textDecoration: 'none', cursor: 'pointer' }}>
            <div className="card" style={{ cursor: 'pointer' }}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = 'var(--border-green)';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = 'var(--shadow-green)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = 'var(--border)';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{
                  width: '52px', height: '52px', borderRadius: '14px',
                  background: 'rgba(0,200,83,0.1)', border: '1px solid rgba(0,200,83,0.2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '24px', flexShrink: 0
                }}>👁</div>
                <div>
                  <div style={{ fontWeight: '700', fontSize: '16px', color: 'var(--text-primary)', marginBottom: '4px' }}>
                    ObsidianEye
                  </div>
                  <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                    Score your video titles, descriptions, thumbnails and keywords
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div onClick={() => setActivePage('guardian')} style={{ textDecoration: 'none', cursor: 'pointer' }}>
            <div className="card" style={{ cursor: 'pointer' }}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = 'var(--border-green)';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = 'var(--shadow-green)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = 'var(--border)';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{
                  width: '52px', height: '52px', borderRadius: '14px',
                  background: 'rgba(0,200,83,0.1)', border: '1px solid rgba(0,200,83,0.2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '24px', flexShrink: 0
                }}>🛡</div>
                <div>
                  <div style={{ fontWeight: '700', fontSize: '16px', color: 'var(--text-primary)', marginBottom: '4px' }}>
                    GuardianAI
                  </div>
                  <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                    Scan your content for platform policy violations before uploading
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div onClick={() => setActivePage('growth')} style={{ textDecoration: 'none', cursor: 'pointer' }}>
            <div className="card" style={{ cursor: 'pointer' }}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = 'var(--border-green)';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = 'var(--shadow-green)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = 'var(--border)';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{
                  width: '52px', height: '52px', borderRadius: '14px',
                  background: 'rgba(0,200,83,0.1)', border: '1px solid rgba(0,200,83,0.2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '24px', flexShrink: 0
                }}>📈</div>
                <div>
                  <div style={{ fontWeight: '700', fontSize: '16px', color: 'var(--text-primary)', marginBottom: '4px' }}>
                    ChannelForge
                  </div>
                  <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                    Build winning channels with AI powered niche and keyword research
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ marginBottom: '32px' }}>
        <h2 style={{
          fontSize: '20px', fontWeight: '700', marginBottom: '16px',
          color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px'
        }}>
          <MdTrendingUp style={{ color: 'var(--primary)' }} /> Trending Videos
        </h2>
        <TrendingVideos setActivePage={setActivePage} />
      </div>

      <div style={{ marginBottom: '32px' }}>
        <h2 style={{
          fontSize: '20px', fontWeight: '700', marginBottom: '16px',
          color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px'
        }}>
          <MdTrendingUp style={{ color: 'var(--primary)' }} /> Quick Actions
        </h2>
        <div className="grid-3">
          <div onClick={() => setActivePage('upload')} className="quick-action-card">
            <MdCloudUpload style={{ fontSize: '32px' }} />
            <span>Upload Video</span>
            <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Add new content</span>
          </div>
          <div onClick={() => setActivePage('channels')} className="quick-action-card">
            <MdBarChart style={{ fontSize: '32px' }} />
            <span>Platform Manager</span>
            <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Manage channels</span>
          </div>
          <div onClick={() => setActivePage('obsidian')} className="quick-action-card ai-action">
            <MdAutoAwesome style={{ fontSize: '32px' }} />
            <span>Obsidian AI</span>
            <span style={{ fontSize: '12px', opacity: 0.8 }}>Generate viral metadata</span>
          </div>
        </div>
      </div>

      <style>{`
        .quick-action-card {
          background: var(--bg-card);
          border: 1px solid var(--border);
          border-radius: 16px;
          padding: 28px 24px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          text-decoration: none;
          color: var(--text-primary);
          font-weight: 600;
          transition: all 0.3s ease;
          text-align: center;
          font-size: 16px;
          cursor: pointer;
        }
        .quick-action-card:hover {
          border-color: var(--primary);
          box-shadow: 0 8px 24px rgba(0,200,83,0.15);
          transform: translateY(-4px);
          color: var(--primary);
        }
        .quick-action-card.ai-action {
          background: linear-gradient(135deg, rgba(0,200,83,0.1), rgba(29,233,182,0.05));
          border-color: rgba(0,200,83,0.3);
          color: var(--primary);
        }
        .quick-action-card.ai-action:hover {
          background: linear-gradient(135deg, rgba(0,200,83,0.2), rgba(29,233,182,0.1));
          box-shadow: 0 8px 24px rgba(0,200,83,0.25);
        }
      `}</style>
    </div>
  );
};

export default Dashboard;